import styled from "styled-components";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Menu from "../components/Menu";
import { useEffect, useState } from "react";
import TimeFooter from "../components/Footer/TimeFooter";
import MenuFooter from "../components/Footer/MenuFooter";
import Confirm from "../components/Footer/Confirm";
import TimeSelect from "../components/TimeSelect";
import ConfirmPage from "../components/ConfirmPage";
import Success from "../components/Success";
import Modify from "../components/Footer/Modify";
import { OrderItemsContext } from "../context/OrderItemsContext";
import { OrderDataContext } from "../context/OrderDataContext";
import {
    AvailableDatesContext,
    LocationsContext,
} from "../context/AvailableDatesContext";
import { ReservationContext } from "../context/ReservationContext";
import { ErrorContext } from "../context/ErrorContext";
import {
    format,
} from "date-fns";
import Api from "../api/api";
import { MenuContext } from "../context/MenuContext";
import { MenuGroupsContext } from "../context/MenuGroupsContext";
import UrlError from "../components/UrlError";
import Checkout from "../components/Checkout";
import { ErrorBoundary } from "react-error-boundary";
import CookingPage from "../components/CookingPage";
import { TranslationContext } from "../context/TranslationContext";

const { utcToZonedTime } = require("date-fns-tz");

const timeZone = "Europe/Vilnius";

function Index () {
    const [appLoading, setAppLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const [translations, setTranslations] = useState({});
    const [orderItems, setOrderItems] = useState({});
    const [page, setPage] = useState("time");
    const [menu, setMenu] = useState([]);
    const [menuGroups, setMenuGroups] = useState([]);
    const [locations, setLocations] = useState([]);
    const [urlError, setUrlError] = useState(false);
    const [today, setToday] = useState(format(new Date(), "y-MM-dd"));
    const [serverTime, setServerTime] = useState(null);
    const [language, setLanguage] = useState(
        localStorage.getItem('boheme-menu-language') ?? 'EN',
    )

    const [availableDates, setAvailableDates] = useState([]);
    const [order, setOrder] = useState({
        date: null,
        time: null,
        location: "to the room",
        comment: null,
        price: 0,
        id: null,
        success: false,
        allowChange: false,
    });

    const [reservation, setReservation] = useState({
        name: null,
        guests: 0,
        room: null,
        checkIn: null,
        checkOut: null,
        code: null,
    });

    useEffect(() => {
        const interval = setInterval(() => {
            const newDay = format(new Date(), "y-MM-dd");
            if (today !== newDay) {
                setToday(newDay);
            }
        }, 60 * 1000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        setAppLoading(true);
        const reservationCode = window.location.hash.replace("#", "");
        Api.getServerTime().then((result) => {
            setServerTime(result);
        });
        Api.log({
            reservation: reservationCode,
            type: "menu-reservation",
            action: "Loading page",
        });
        Api.notify({
            text: "Loading page...",
            data: {
                code: reservationCode,
            },
        });
        if (reservationCode) {
            Api.getReservation(reservationCode)
                .then((reservation) => {
                    const data = {
                        name: reservation.guest_name,
                        room: reservation.room,
                        guests: Number(reservation.guest_count),
                        checkIn: utcToZonedTime(reservation.check_in, timeZone),
                        checkOut: utcToZonedTime(reservation.check_out, timeZone),
                        code: reservationCode,
                        datesInSelector: reservation.datesInSelector,
                        firstDaySelectTime: reservation.firstDaySelectTime,
                    };

                    if (
                        data.name === "" ||
                        data.room === "" ||
                        data.guests === 0 ||
                        data.checkIn === "" ||
                        data.checkOut === ""
                    ) {
                        setUrlError(true);
                        setAppLoading(false);
                    } else {
                        setReservation(data);
                        const tempDates = data.datesInSelector;
                        Api.log({
                            reservation: reservationCode,
                            type: "menu-reservation",
                            action: "Reservation loaded",
                            additionalData: JSON.stringify({
                                name: reservation.guest_name,
                                room: reservation.room,
                                guests: Number(reservation.guest_count),
                                checkIn: utcToZonedTime(reservation.check_in, timeZone),
                                checkOut: utcToZonedTime(reservation.check_out, timeZone),
                                code: reservationCode,
                                datesInSelector: reservation.datesInSelector,
                                firstDaySelectTime: reservation.firstDaySelectTime,
                            }),
                        });
                        Api.notify({
                            text: "Reservation loaded",
                            data: {
                                reservationCode: reservationCode,
                                name: reservation.guest_name,
                                checkIn: reservation.check_in,
                                checkOut: reservation.check_out,
                                availableTimes: tempDates.join(", "),
                            },
                        });

                        setAvailableDates(tempDates);
                        if (tempDates.length > 0) {
                            setOrder((order) => ({
                                ...order,
                                ...{ date: tempDates[0] },
                            }));
                            Api.log({
                                reservation: reservationCode,
                                type: "menu-reservation",
                                action: "Showing date",
                                orderDate: tempDates[0],
                                additionalData: JSON.stringify({
                                    checkIn: utcToZonedTime(reservation.check_in, timeZone),
                                    checkOut: utcToZonedTime(reservation.check_out, timeZone),
                                }),
                            });
                        } else {
                            Api.log({
                                reservation: reservationCode,
                                type: "menu-reservation",
                                action: "No dates in selector",
                                additionalData: JSON.stringify({
                                    datesInSelector: reservation.datesInSelector,
                                    firstDaySelectTime: reservation.firstDaySelectTime,
                                }),
                            });
                        }

                        loadMenu(language)

                        Api.getLocations()
                            .then((response) => {
                                const locationExists = response.find(location => location.location === order.location)
                                if (!locationExists && !order.id) {
                                    setOrder((order) => ({
                                        ...order,
                                        ...{ location: response[0]?.location ?? '' },
                                    }));
                                }

                                setLocations(response);
                            })
                            .finally(() => {
                                setAppLoading(false);
                            });
                    }
                })
                .catch((e) => {
                    Api.log({
                        reservation: reservationCode,
                        type: "menu-reservation",
                        action: "Reservation not found",
                    });
                    window.location.href = "https://reservations.bohemehouse.lt/";
                })
                .finally(() => {
                    setAppLoading(false);
                });
        } else {
            Api.log({
                type: "menu-reservation",
                action: "Reservation code not provided",
            });
            window.location.href = "https://reservations.bohemehouse.lt/";
            throw new Error("Wrong url, redirecting..");
        }
        Api.loadTranslations({ lang: language }).then((response) => {
            setTranslations(response);
        })

    }, [today]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [page]);

    const loadMenu = (langToLoad, orderId = null) => {
        Api.getMenu(langToLoad, orderId).then((response) => {
            setMenu(response.items);
            setMenuGroups(response.groups);
        });
    }

    const restoreOrder = () => {
        Api.getOrder(reservation.code, order.date, language).then((orderDB) => {
            if (orderDB.id) {
                let formattedItems = {};
                orderDB.items.forEach((item) => {
                    formattedItems[item.id] = {
                        quantity: item.quantity,
                        children: {},
                    };

                    const childrenKeys = Object.keys(item.children)
                    formattedItems[item.id].children = {};

                    childrenKeys.forEach((key) => {
                        item.children[key].forEach((child) => {
                            if (!formattedItems[item.id].children[child.child_index]) {
                                formattedItems[item.id].children[child.child_index] = {};
                            }
                            formattedItems[item.id].children[child.child_index][child.id] = {
                                quantity: child.quantity,
                                price: child.price,
                                title: child.title,
                                description: child.description,
                            };
                        });
                    });
                });

                setOrder((currentOrder) => ({
                    ...currentOrder,
                    ...{
                        time: orderDB.time,
                        location: orderDB.location,
                        comment: orderDB.comment,
                        price: parseFloat(orderDB.total_price),
                        id: orderDB.id,
                        success: false,
                        status: orderDB.status,
                        allowChange: orderDB.allowChange ?? false,
                    },
                }));
                loadMenu(language, orderDB.id)
                setOrderItems(formattedItems);
                if (order.date === availableDates[0] && !orderDB.allowChange) {
                    setPage("cooking");
                    Api.log({
                        reservation: reservation.code,
                        order: orderDB.id,
                        type: "menu-reservation",
                        action: "Existing order loaded",
                        orderDate: order.date,
                        additionalData: JSON.stringify({
                            view: "cooking",
                        }),
                    });
                } else {
                    setPage("menu");
                    Api.log({
                        reservation: reservation.code,
                        order: orderDB.id,
                        type: "menu-reservation",
                        action: "Existing order loaded",
                        orderDate: order.date,
                        additionalData: JSON.stringify({
                            view: "menu",
                        }),
                    });
                }
            } else {
                loadMenu(language)
                setPage("time");
                Api.log({
                    reservation: reservation.code,
                    type: "menu-reservation",
                    action: "Existing order not found",
                    orderDate: order.date,
                    additionalData: JSON.stringify({
                        view: "time",
                    }),
                });
            }
        });
    };

    useEffect(() => {
        if (appLoading === false && urlError === false) {
            restoreOrder();
        }
    }, [order.date]);

    useEffect(() => {
        recalculatePrice();
    }, [JSON.stringify(orderItems), menu.length]);

    const recalculatePrice = () => {
        let totalPrice = 0

        for (const [id, data] of Object.entries(orderItems)) {
            const item = menu.find(element => element.id === Number(id))

            if (item) {
                totalPrice += item.price * data.quantity
            }
            if (data.children) {
                for (const [index, children] of Object.entries(data.children)) {
                    for (const [childId, childData] of Object.entries(children)) {
                        if (childData.quantity > 0) {
                            totalPrice += childData.price * childData.quantity
                        }
                    }
                }
            }
        }
        setOrder((prev) => ({ ...prev, price: totalPrice }))
    }

    const renderFooter = () => {
        if (page === "time") {
            return <TimeFooter setPage={setPage} />;
        } else if (page === "menu") {
            return <MenuFooter setPage={setPage} />;
        } else if (page === "confirm") {
            return <Confirm setPage={setPage} />;
        } else if (page === "success") {
            return <Modify setPage={setPage} />;
        }
    };

    const renderPage = () => {
        if (urlError) {
            return <UrlError />;
        }

        if (availableDates.length === 0) {
            return <Checkout />;
        }

        switch (page) {
            case "menu":
                return <Menu page={page} />;
            case "time":
                return <TimeSelect items={menu} page={page} />;
            case "confirm":
                return <ConfirmPage items={menu} page={page} />;
            case "success":
                return <Success setPage={setPage} page={page} />;
            case "cooking":
                return <CookingPage setPage={setPage} page={page} />;
        }
    };

    if (appLoading) {
        return (
            <Loading>
                <div>Loading...</div>
            </Loading>
        );
    }

    function ErrorFallback ({ error, resetErrorBoundary }) {
        return (
            <div role="alert">
                <p>Something went wrong:</p>
                <pre>{error.message}</pre>
                <button onClick={resetErrorBoundary}>Try again</button>
            </div>
        );
    }

    const handleChangeLanguage = () => {
        const langToSet = language === 'EN' ? 'LT' : 'EN'
        setLanguage(langToSet);
        loadMenu(langToSet, order.id)
        localStorage.setItem('boheme-menu-language', langToSet)
        Api.loadTranslations({ lang: langToSet }).then((response) => {
            setTranslations(response);
        })
    }

    return (
        <Layout>
            <ErrorContext.Provider value={{ errors, setErrors }}>
                <TranslationContext.Provider value={{ translations, setTranslations }}>
                    <MenuGroupsContext.Provider value={{ menuGroups }}>
                        <LocationsContext.Provider value={{ locations, setLocations }}>
                            <MenuContext.Provider value={{ menu, setMenu }}>
                                <ReservationContext.Provider
                                    value={{ reservation, setReservation }}
                                >
                                    <AvailableDatesContext.Provider
                                        value={{ availableDates, setAvailableDates }}
                                    >
                                        <OrderDataContext.Provider value={{ order, setOrder }}>
                                            <OrderItemsContext.Provider
                                                value={{ orderItems, setOrderItems }}
                                            >
                                                <ErrorBoundary
                                                    FallbackComponent={ErrorFallback}
                                                    onReset={() => {
                                                        // reset the state of your app so the error doesn't happen again
                                                    }}
                                                >
                                                    <Header page={page} setPage={setPage} language={language} changeLanguage={handleChangeLanguage} />
                                                    <Content>{renderPage()}</Content>
                                                    {!urlError && <Footer>{renderFooter()}</Footer>}
                                                </ErrorBoundary>
                                            </OrderItemsContext.Provider>
                                        </OrderDataContext.Provider>
                                    </AvailableDatesContext.Provider>
                                </ReservationContext.Provider>
                            </MenuContext.Provider>
                        </LocationsContext.Provider>
                    </MenuGroupsContext.Provider>
                </TranslationContext.Provider>
            </ErrorContext.Provider>
        </Layout>
    );
}

const Loading = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  height: 100vh;
  text-align: center;
`;

const Layout = styled.div`
  display: flex;
  flex: 1;
  min-height: 100vh;
  width: 100%;
  padding: 100px 15px 150px 15px;
`;

const Content = styled.div`
  display: flex;
  flex: 1;
  min-height: 100%;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;

  & > div {
    display: flex;
    width: 100%;
  }
`;

export default Index;
